import Vue from 'vue';
import { updateAndFetchBill, fetchBill } from '@/services/bill.service';

const state = {
    id: null,
    tip: null,
    posConnectionError: false,
    amountSelected: 0,
    selectedSplit: {},
    bill: null
};

const getters = {
    showMobileBasket(state) {
        return state.showMobileBasket;
    },
    posConnectionError(state) {
        return state.posConnectionError;
    },
    tip: state => {
        return state.tip || 0;
    },
    id: state => {
        return state.id;
    },
    bill: state => {
        return state.bill;
    },
    discountAmount: state => {
        return state?.bill?.discountAmount ?? 0;
    },
    serviceCharge: state => {
        return state?.bill?.serviceCharge ?? 0;
    },
    billItems: state => {
        return state?.bill?.items || [];
    },
    billRewards: state => {
        return state?.bill?.rewards || [];
    },
    availablePatTips: (state, getters, rootState) => {
        const { venue } = rootState.venue;
        if (venue.acceptPatTips === 'disabled') return null;
        if (venue.acceptPatTips === 'enabled') return venue.tipPatPercentages;
        if (venue.acceptPatTips === 'smart') {
            if (getters.subtotal < 1000) return [0, 0.5, 1, 2];
            if (getters.subtotal < 3000) return [0, 1, 2, 4];
            return [0, 2, 4, 6];
        }
    },
    amountSelected: state => {
        return state.amountSelected;
    },
    selectedSplit: state => {
        return state.selectedSplit;
    },
    subtotal: (state, getters) => {
        return state.amountSelected + getters.discountAmount;
    },
    total: state => {
        return state.bill?.amountDue || 0;
    },
    totalSelectedToPay: (state, getters) => {
        return state.amountSelected + getters.tip;
    }
};

const mutations = {
    setBillId(state, billId) {
        state.id = billId;
    },
    setTip(state, val) {
        state.tip = val;
    },
    setBill(state, val) {
        state.bill = val;
    },
    setAmountSelected(state, amount) {
        state.amountSelected = amount;
    },
    setSelectedSplit(state, split) {
        state.selectedSplit = split;
    }
};

const actions = {
    setTip({ commit }, tip) {
        commit('setTip', tip);
    },
    initTipValue({ state, commit, getters, rootGetters }, prevAmount) {
        const tipVariant = rootGetters['venue/tipPatVariant'];
        const availableTips = getters.availablePatTips;
        const amount = getters.amountSelected;
        const currentTip = state.tip;

        if (!availableTips?.length || currentTip === 0) {
            return;
        }

        const calculatedTipValue = tip => {
            return tipVariant === 'smart'
                ? Math.round(tip * 100)
                : Math.round((amount * tip) / 10000);
        };

        const currentTipValue =
            tipVariant === 'smart'
                ? currentTip / 100
                : (currentTip * 10000) / prevAmount;

        if (availableTips.includes(currentTipValue)) {
            commit('setTip', calculatedTipValue(currentTipValue));
        } else {
            commit('setTip', calculatedTipValue(availableTips[0]));
        }
    },

    async updateAndFetchBill(
        { commit, rootState },
        { venueId, tableId, email }
    ) {
        const { data } = await updateAndFetchBill({
            venueId,
            tableId,
            email
        });

        if (data.id) {
            commit('setBillId', data.id);
        }

        commit('setBill', data);

        if (
            data &&
            rootState.venue.venue.visualMenu &&
            Vue.prototype.$growthbook &&
            Vue.prototype.$growthbook.isOn('add_to_bill')
        ) {
            commit('venue/SET_VISUAL_MENU', false, { root: true });
        }

        return data;
    },
    async fetchBill({ commit, rootState }, { venueId, billId, email }) {
        const { data } = await fetchBill({
            venueId,
            billId,
            email
        });

        commit('setBill', data);

        if (
            data &&
            rootState.venue.venue.visualMenu &&
            Vue.prototype.$growthbook &&
            Vue.prototype.$growthbook.isOn('add_to_bill')
        ) {
            commit('venue/SET_VISUAL_MENU', false, { root: true });
        }

        return data;
    },
    setAmountSelected({ commit }, amount) {
        commit('setAmountSelected', amount);
    },
    setSelectedSplit({ commit }, split) {
        commit('setSelectedSplit', split);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
